import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { fetchAllIn, fetchAllInResult } from '../lib/adminCounts';
import { fetchAllRows, fetchAllRowsResult } from '../../lib/fetchAll';
import { escapeLike } from '../../lib/tropeUtils';
import InfoTip from '../components/InfoTip';
import { adminVoteSummary } from '../../lib/pipelineVote';
import { classifyPopularity, readingsText, POPULARITY_LABEL } from '../../lib/popularity';

function timeAgo(ts) {
  if (!ts) return '—';
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7)  return `${d}d ago`;
  if (d < 30) return `${Math.floor(d / 7)}w ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

function CoverThumb({ coverUrl, color = '#4a1942', accent = '#d8b4fe', sz = 48 }) {
  if (coverUrl) {
    return (
      <div style={{ width: sz, height: sz * 1.48, borderRadius: '3px 5px 5px 3px', overflow: 'hidden', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
        <img src={coverUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={e => { e.target.style.display = 'none'; }} />
      </div>
    );
  }
  return (
    <div style={{ width: sz, height: sz * 1.48, borderRadius: '3px 5px 5px 3px', background: `linear-gradient(155deg,${color},${accent}55)`, flexShrink: 0, boxShadow: `0 2px 8px ${color}35`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span style={{ fontSize: sz * 0.35, opacity: 0.5 }}>📚</span>
    </div>
  );
}

// Which pipeline columns a given book slot maps to — used both for reading
// current values into the edit form and for building the update payload.
const SLOT_COLUMNS = {
  pop: { title: 'pop_book_title', author: 'pop_book_author', cover: 'pop_cover_url' },
  mid: { title: 'mid_book_title', author: 'mid_book_author', cover: 'mid_cover_url' },
  gem: { title: 'gem_book_title', author: 'gem_book_author', cover: 'gem_cover_url' },
};

// Why an approved pipeline can never show on the homepage, and what to do about
// it. The reasons come from the database (admin_pipeline_homepage_flags,
// migration 20261010100000), so this screen and the homepage cannot disagree.
const WONT_SHOW = {
  title_blank:    { text: 'A title is blank, so the card has nothing to show.',                                  fix: 'edit' },
  gem_not_linked: { text: "The gem isn't linked to a book in the catalogue, so it can't be matched to a craving. Edit the gem's title and author to match the catalogue book exactly. It links when you save, if exactly one live book matches. Two books with the same title and author can't be linked this way, and the book needs a description from its author or the save is refused.", fix: 'edit' },
  gem_not_live:   { text: "The gem book isn't live.",                                                            fix: 'books' },
  gem_no_data:    { text: 'The gem book has no matching data yet, so it can never be matched to a craving.',     fix: 'health' },
};

function WontShowNote({ reasons, onEdit, onNavigate, editing, gemBookId }) {
  const btn = { padding: '4px 10px', background: 'var(--brand-white)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xxs)', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' };
  return (
    <div style={{ background: 'var(--feedback-warning-bg)', border: '1px solid var(--feedback-warning-border)', borderRadius: 'var(--radius-10)', padding: '10px 12px', marginBottom: 'var(--space-14)' }}>
      <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--feedback-warning-ink)', margin: '0 0 6px' }}>Won&apos;t show on the homepage</p>
      {reasons.map(r => {
        const w = WONT_SHOW[r];
        if (!w) return null;
        return (
          <div key={r} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
            <span style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--feedback-warning-ink)' }}>{w.text}</span>
            {w.fix === 'edit' && !editing && <button type="button" onClick={onEdit} style={btn}>Edit</button>}
            {w.fix === 'books' && (gemBookId
              ? <button type="button" onClick={() => onNavigate?.(`r-books?book=${gemBookId}`)} style={btn}>Open book</button>
              : <button type="button" onClick={() => onNavigate?.('r-books')} style={btn}>Find in Books</button>)}
            {w.fix === 'health' && <button type="button" onClick={() => onNavigate?.('health')} style={btn}>Open in Health</button>}
          </div>
        );
      })}
    </div>
  );
}

// Ticket 39: an anchor is meant to be the well-known book, so its popularity
// class is shown and anything that is not Popular is flagged. `anchor` is
// undefined until read, { error } when the read failed, { unlinked } when the
// pipeline is not tied to a book row, or the book itself.
function AnchorClass({ anchor }) {
  if (anchor === undefined) return null;
  const small = { fontSize: 'var(--font-size-xxs)', margin: '2px 0 0', lineHeight: 1.4 };
  if (anchor.error) return <p data-anchor-class style={{ ...small, color: 'var(--feedback-warning-ink)' }}>Could not read its popularity.</p>;
  const flag = (
    <p data-anchor-flag style={{ ...small, fontWeight: 700, color: 'var(--feedback-warning-ink)' }}>
      Not Popular. An anchor should be the well-known book.
    </p>
  );
  if (anchor.unlinked) return (
    <>
      <p data-anchor-class style={{ ...small, color: 'var(--neutral-500)' }}>Popularity: not tied to a book on Reanlo, so unknown.</p>
      {flag}
    </>
  );
  const pop = classifyPopularity(anchor.book);
  return (
    <>
      <p data-anchor-class style={{ ...small, color: 'var(--neutral-500)' }}>
        {POPULARITY_LABEL[pop.cls]}{pop.byHand ? ' (set by hand)' : ''} · {readingsText(anchor.book)}
      </p>
      {pop.cls !== 'popular' && flag}
    </>
  );
}

function PipelineCard({ pipeline, working, onAction, onEditSave, isMobile, reasons, votes, onNavigate, anchor }) {
  const busy = working === pipeline.id;
  const [editing, setEditing] = useState(false);
  const [editValues, setEditValues] = useState(null);
  const [saving, setSaving] = useState(false);

  const slots = [
    pipeline.pop_book_title && { slot: 'pop', label: '📚 Popular',     title: pipeline.pop_book_title, author: pipeline.pop_book_author, coverUrl: pipeline.pop_cover_url },
    pipeline.mid_book_title && { slot: 'mid', label: '👀 On the Rise', title: pipeline.mid_book_title, author: pipeline.mid_book_author, coverUrl: pipeline.mid_cover_url },
    pipeline.gem_book_title && { slot: 'gem', label: '💎 Hidden Gem',  title: pipeline.gem_book_title, author: pipeline.gem_book_author, coverUrl: pipeline.gem_cover_url },
  ].filter(Boolean);

  const missingCover = slots.some(s => !s.coverUrl);
  const voteLine = adminVoteSummary(votes);

  const statusColors = {
    pending:  { bg: '#fef3c7', color: 'var(--feedback-warning-ink)', border: 'var(--feedback-warning-border)' },
    approved: { bg: '#d1fae5', color: '#065f46', border: '#a7f3d0' },
    rejected: { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
    hidden:   { bg: '#f3f4f6', color: '#4b5563', border: '#d1d5db' },
  };
  const sc = statusColors[pipeline.status] || statusColors.pending;

  function startEdit() {
    const values = {};
    slots.forEach(s => {
      values[s.slot] = { title: s.title || '', author: s.author || '', coverUrl: s.coverUrl || '' };
    });
    setEditValues(values);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setEditValues(null);
  }

  function updateField(slot, field, value) {
    setEditValues(prev => ({ ...prev, [slot]: { ...prev[slot], [field]: value } }));
  }

  async function saveEdit() {
    setSaving(true);
    try {
      await onEditSave(pipeline, editValues);
      setEditing(false);
      setEditValues(null);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ background: 'var(--brand-white)', border: missingCover && !editing ? '1.5px solid var(--feedback-warning-border)' : '1px solid var(--brand-sand)', borderRadius: 'var(--radius-xl)', padding: isMobile ? '14px' : '18px 20px', marginBottom: 'var(--space-md)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-md)', gap: 'var(--space-10)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {pipeline.vibe_tags?.length > 0 && (
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 'var(--space-6)' }}>
              <span style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--brand-rust)', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>if you love</span>
              {pipeline.vibe_tags.map(v => (
                <span key={v} style={{ fontSize: 'var(--font-size-xxs)', padding: '2px 8px', borderRadius: 'var(--radius-10)', background: '#FBF7F0', color: '#8b7355', border: '1px solid #ece3d4' }}>{v}</span>
              ))}
            </div>
          )}
          {pipeline.phrase && (
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-base)', fontStyle: pipeline.phrase_format === 'quote' ? 'italic' : 'normal', color: 'var(--neutral-800)', margin: '0 0 3px', lineHeight: 1.4 }}>
              "{pipeline.phrase}"
            </p>
          )}
          {pipeline.phrase_format === 'quote' && pipeline.quote_source && (
            <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: '0 0 3px' }}>— {pipeline.quote_source}</p>
          )}
          <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>
            Submitted {timeAgo(pipeline.created_at)}
            {pipeline.submitted_by && <> · by @{pipeline.submitted_by}</>}
            {pipeline.entry_point && <> · {pipeline.entry_point}</>}
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, flexShrink: 0 }}>
          <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '3px 10px', borderRadius: 'var(--radius-xxl)', background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>
            {pipeline.status}
          </span>
          {missingCover && !editing && (
            <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '3px 10px', borderRadius: 'var(--radius-xxl)', background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', border: '1px solid var(--feedback-warning-border)' }}>
              ⚠ Missing cover
            </span>
          )}
        </div>
      </div>

      {voteLine && (
        <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', margin: '0 0 var(--space-md)' }}>
          Votes: {voteLine.counts}
          {voteLine.place && <> · {voteLine.place}</>}
          {voteLine.overCap && (
            <span style={{ marginLeft: 8, fontWeight: 700, color: 'var(--feedback-warning-ink)' }}>
              Over the cap of {voteLine.cap}, so readers don&apos;t see it
            </span>
          )}
        </p>
      )}

      {reasons?.length > 0 && (
        <WontShowNote reasons={reasons} editing={editing} onEdit={startEdit} onNavigate={onNavigate} gemBookId={pipeline.gem_book_id} />
      )}

      {/* Books — read view or edit view */}
      {!editing ? (
        <div style={{ display: 'flex', gap: isMobile ? 8 : 12, marginBottom: 'var(--space-14)', overflowX: isMobile ? 'auto' : 'visible' }}>
          {slots.map((book, i) => (
            <div key={i} style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'flex-start', background: 'var(--brand-paper)', borderRadius: 'var(--radius-10)', padding: '8px 10px', flexShrink: 0, minWidth: isMobile ? 140 : 'auto', flex: isMobile ? '0 0 auto' : 1 }}>
              <CoverThumb coverUrl={book.coverUrl} sz={36} />
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, color: 'var(--brand-rust)', margin: '0 0 2px', letterSpacing: 0.5 }}>{book.label}</p>
                <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: isMobile ? 110 : 160 }}>{book.title}</p>
                <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', margin: 'var(--space-none)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: isMobile ? 110 : 160 }}>{book.author || '—'}</p>
                {book.slot === 'pop' && <AnchorClass anchor={anchor} />}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)', marginBottom: 'var(--space-14)' }}>
          {slots.map((book, i) => (
            <div key={i} style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'flex-start', background: 'var(--brand-paper)', borderRadius: 'var(--radius-10)', padding: '10px 12px' }}>
              <CoverThumb coverUrl={editValues[book.slot]?.coverUrl} sz={36} />
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
                <p style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, color: 'var(--brand-rust)', margin: 'var(--space-none)', letterSpacing: 0.5 }}>{book.label}</p>
                <input
                  value={editValues[book.slot]?.title || ''}
                  onChange={e => updateField(book.slot, 'title', e.target.value)}
                  placeholder="Title"
                  style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--brand-sand)', borderRadius: 8, fontSize: 'var(--font-size-xs)', outline: 'none', boxSizing: 'border-box' }}
                />
                <input
                  value={editValues[book.slot]?.author || ''}
                  onChange={e => updateField(book.slot, 'author', e.target.value)}
                  placeholder="Author"
                  style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--brand-sand)', borderRadius: 8, fontSize: 'var(--font-size-xs)', outline: 'none', boxSizing: 'border-box' }}
                />
                <input
                  value={editValues[book.slot]?.coverUrl || ''}
                  onChange={e => updateField(book.slot, 'coverUrl', e.target.value)}
                  placeholder="Cover image URL"
                  style={{ width: '100%', padding: '6px 10px', border: !editValues[book.slot]?.coverUrl ? '1.5px solid var(--feedback-warning-border)' : '1px solid var(--brand-sand)', borderRadius: 8, fontSize: 'var(--font-size-xs)', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
        {editing ? (
          <>
            <button onClick={saveEdit} disabled={saving}
              style={{ padding: '8px 16px', background: '#d1fae5', color: '#065f46', border: '1.5px solid #a7f3d0', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-sm)', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.5 : 1, fontFamily: 'var(--font-body)' }}>
              {saving ? 'Saving...' : '✓ Save changes'}
            </button>
            <button onClick={cancelEdit} disabled={saving}
              style={{ padding: '8px 16px', background: 'var(--brand-white)', color: 'var(--neutral-400)', border: '1.5px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-body)' }}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <button onClick={startEdit}
              style={{ padding: '8px 16px', background: 'var(--brand-white)', color: 'var(--neutral-600)', border: '1.5px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
              ✎ Edit
            </button>
            {pipeline.status !== 'approved' && (
              <button onClick={() => onAction('approve', pipeline)} disabled={busy}
                style={{ padding: '8px 16px', background: '#d1fae5', color: '#065f46', border: '1.5px solid #a7f3d0', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-sm)', fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.5 : 1, fontFamily: 'var(--font-body)' }}>
                {busy ? '...' : '✓ Approve'}
              </button>
            )}
            {pipeline.status !== 'rejected' && (
              <button onClick={() => onAction('reject', pipeline)} disabled={busy}
                style={{ padding: '8px 16px', background: 'var(--feedback-error-bg)', color: '#991b1b', border: '1.5px solid #fca5a5', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-sm)', fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.5 : 1, fontFamily: 'var(--font-body)' }}>
                {busy ? '...' : '✕ Reject'}
              </button>
            )}
            {pipeline.status === 'approved' && (
              <button onClick={() => onAction('hide', pipeline)} disabled={busy}
                style={{ padding: '8px 16px', background: '#f3f4f6', color: '#4b5563', border: '1.5px solid #d1d5db', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-sm)', fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.5 : 1, fontFamily: 'var(--font-body)' }}>
                Hide
              </button>
            )}
            {pipeline.status !== 'pending' && (
              <button onClick={() => onAction('pending', pipeline)} disabled={busy}
                style={{ padding: '8px 16px', background: 'var(--brand-white)', color: 'var(--neutral-400)', border: '1.5px solid var(--brand-sand)', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.5 : 1, fontFamily: 'var(--font-body)' }}>
                Reset to pending
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatsBar({ counts, isMobile }) {
  const stats = [
    { label: 'Pending review', value: counts.pending,  color: 'var(--feedback-warning-ink)', bg: '#fef3c7', tip: 'Pipelines a reader has built and submitted, or that are missing a cover, that no admin has actioned yet.' },
    { label: 'Approved',       value: counts.approved, color: '#065f46', bg: '#d1fae5', tip: 'Pipelines an admin has approved. These can surface to readers.' },
    { label: 'Rejected',       value: counts.rejected, color: '#991b1b', bg: '#fee2e2', tip: 'Pipelines an admin turned down. Kept for the record, never shown to readers.' },
    { label: 'Total',          value: counts.total,    color: 'var(--neutral-800)', bg: '#f5f5f4', tip: 'Every pipeline row, whatever its status: pending, approved, rejected and hidden added together.' },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 'var(--space-10)', marginBottom: 'var(--space-xl)' }}>
      {stats.map(s => (
        <div key={s.label} style={{ background: s.bg, borderRadius: 'var(--radius-lg)', padding: '12px 16px', border: `1px solid ${s.color}20` }}>
          <p style={{ fontSize: 22, fontWeight: 700, color: s.color, margin: '0 0 2px', fontFamily: 'var(--font-body)' }}>{s.value}</p>
          <p style={{ fontSize: 11, color: s.color, margin: 0, opacity: 0.75, display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>{s.label}</span><InfoTip text={s.tip} width={210} />
          </p>
        </div>
      ))}
    </div>
  );
}

export default function AdminPipelinesSection({ onBadgeChange, onNavigate, isMobile }) {
  const [pipelines, setPipelines] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [tab,       setTab]       = useState('pending');
  const [working,   setWorking]   = useState(null);
  // Approved pipelines that can never show on the homepage: id -> reasons, and
  // a server-side count (a count over the whole set, not over a page of it).
  const [flags,      setFlags]      = useState({});
  const [flagCount,  setFlagCount]  = useState(null);
  const [flagError,  setFlagError]  = useState(null);
  const [wontShow,   setWontShow]   = useState(false);
  // Ticket 26 B: vote counts and "N of M under this anchor", one row per
  // pipeline (admin_pipeline_votes, migration 20261012100000). A failed read
  // says so on screen; it must not look like "nobody has voted".
  const [votes,      setVotes]      = useState({});
  const [voteError,  setVoteError]  = useState(null);
  // Ticket 39: each anchor's popularity, keyed by pipeline id.
  const [anchors,    setAnchors]    = useState({});

  // ✅ keep onBadgeChange in a ref so it never needs to be a useCallback dep
  const onBadgeChangeRef = useRef(onBadgeChange);
  useEffect(() => { onBadgeChangeRef.current = onBadgeChange; }, [onBadgeChange]);

  // ✅ empty dep array — stable, never re-fires on parent re-renders
  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Paged. The status counts below are .filter().length over this array,
      // so a bare select would cap the list at 1,000 and every count with it —
      // silently, and only once the table got big enough to matter.
      const data = await fetchAllRows(() => supabase
        .from('pipelines')
        .select(`
          id, status, phrase, phrase_format, quote_source, vibe_tags, created_at, entry_point,
          pop_book_title, pop_book_author, pop_cover_url,
          mid_book_title, mid_book_author, mid_cover_url,
          gem_book_title, gem_book_author, gem_cover_url,
          pop_book_id, gem_book_id, created_by
        `));
      // fetchAllRows pages by id, so restore newest-first for the list.
      data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      // Fetch submitter usernames
      const userIds = [...new Set((data || []).map(p => p.created_by).filter(Boolean))];
      let usernameMap = {};
      if (userIds.length > 0) {
        const profiles = await fetchAllIn('user_profiles', 'id, user_id, username', 'user_id', userIds);
        (profiles || []).forEach(p => { usernameMap[p.user_id] = p.username; });
      }

      const enriched = (data || []).map(p => ({
        ...p,
        submitted_by: p.entry_point === 'admin-seeded' ? null : (usernameMap[p.created_by] || null),
      }));

      setPipelines(enriched);

      // Which approved pipelines can never show, and why. A failed read says
      // so on screen; it must not look like "nothing is flagged".
      const flagRes = await fetchAllRowsResult(() => supabase
        .from('admin_pipeline_homepage_flags')
        .select('id, reasons'));
      const { count: flagTotal, error: flagCountErr } = await supabase
        .from('admin_pipeline_homepage_flags')
        .select('id', { count: 'exact', head: true });
      if (!flagRes.ok || flagCountErr) {
        setFlagError(flagRes.error || flagCountErr.message);
        setFlags({});
        setFlagCount(null);
      } else {
        setFlagError(null);
        setFlags(Object.fromEntries(flagRes.rows.map(r => [r.id, r.reasons])));
        setFlagCount(flagTotal);
      }

      const voteRes = await fetchAllRowsResult(() => supabase
        .from('admin_pipeline_votes')
        .select('pipeline_id, up_votes, down_votes, net_votes, anchor_rank, anchor_total, within_cap, cap'),
        { orderColumn: 'pipeline_id' });
      if (!voteRes.ok) {
        setVoteError(voteRes.error);
        setVotes({});
      } else {
        setVoteError(null);
        setVotes(Object.fromEntries(voteRes.rows.map(r => [r.pipeline_id, r])));
      }

      const popIds = [...new Set(enriched.map(p => p.pop_book_id).filter(Boolean))];
      const anchorRes = popIds.length
        ? await fetchAllInResult('books', 'id, edition_count, ratings_count, popularity_checked_at, popularity_override:book_popularity_overrides ( class, reason )', 'id', popIds)
        : { ok: true, rows: [] };
      const bookById = Object.fromEntries(anchorRes.rows.map(b => [b.id, b]));
      setAnchors(Object.fromEntries(enriched.map(p => [p.id,
        !anchorRes.ok ? { error: true }
          : !p.pop_book_id || !bookById[p.pop_book_id] ? { unlinked: true }
          : { book: bookById[p.pop_book_id] }])));

      // Update sidebar badge with pending count
      const pendingCount = enriched.filter(p => p.status === 'pending').length;
      onBadgeChangeRef.current?.('pipelines', pendingCount);
    } catch (err) {
      console.error('AdminPipelinesSection error:', err.message);
    } finally {
      setLoading(false);
    }
  }, []); // ✅ empty — no deps needed thanks to ref

  useEffect(() => { load(); }, [load]);

  async function handleAction(action, pipeline) {
    setWorking(pipeline.id);
    try {
      const newStatus = action === 'approve' ? 'approved'
        : action === 'reject'  ? 'rejected'
        : action === 'hide'    ? 'hidden'
        : 'pending';
      const { error } = await supabase.from('pipelines').update({ status: newStatus }).eq('id', pipeline.id);
      if (error) throw error;
      await load();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setWorking(null);
    }
  }

  // Saves edited title/author/cover for each book slot on the pipeline
  // row itself. Also best-effort syncs the cover onto the matching row in
  // `books` (one row, matched by title+author, and only where that book has
  // no cover) so a cover found here isn't lost the next time this book
  // shows up in some other pipeline or in the catalog generally. Pipeline
  // save always happens and always reports success/failure; the books
  // sync is a bonus and failures there are logged, not surfaced —
  // getting the card itself fixed is the actual point of this action.
  async function handleEditSave(pipeline, editValues) {
    const updates = {};
    Object.entries(SLOT_COLUMNS).forEach(([slot, cols]) => {
      if (!editValues[slot]) return;
      updates[cols.title]  = editValues[slot].title || null;
      updates[cols.author] = editValues[slot].author || null;
      updates[cols.cover]  = editValues[slot].coverUrl || null;
    });

    const { error } = await supabase.from('pipelines').update(updates).eq('id', pipeline.id);
    if (error) {
      alert('Failed to save: ' + error.message);
      throw error;
    }

    // Best-effort: push a newly-added cover back onto the books table too.
    //
    // Two steps rather than one filtered update. An update carrying its own
    // .ilike() filter writes to every row the filter matches, and there is no
    // .limit() on it — so a title holding a % (a LIKE wildcard, and PostgREST
    // reads * the same way) would stamp this one cover onto every coverless
    // book the wildcard reached. Resolving the id first bounds the write to a
    // single row and makes the blast radius visible at the call site.
    //
    // .order('id').limit(1) matches how the rest of the codebase resolves a
    // book by title+author (findOrCreateBook.js, lookupActiveTropeId), so
    // where duplicate rows exist every path converges on the same oldest one
    // instead of scattering.
    for (const slot of Object.keys(SLOT_COLUMNS)) {
      const v = editValues[slot];
      if (!v?.coverUrl || !v?.title) continue;

      // books.author_name is NOT NULL, so a slot with no author can only ever
      // be matched on title alone — which is how a cover lands on the wrong
      // book. Skipping is what the previous code did in practice anyway: it
      // passed '' to .ilike(), which matches only a literally empty
      // author_name. This says so instead of looking like it works.
      if (!v.author) {
        console.warn(`Skipped cover back-sync for "${v.title}": no author on this slot, and title alone is not specific enough to match a book.`);
        continue;
      }

      const { data: target, error: findErr } = await supabase
        .from('books')
        .select('id')
        .ilike('title', escapeLike(v.title))
        .ilike('author_name', escapeLike(v.author))
        .is('cover_image_url', null)
        .order('id', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (findErr) {
        console.warn(`Could not look up book for cover back-sync "${v.title}":`, findErr.message);
        continue;
      }
      // No row is the ordinary case — the book already has a cover, or is not
      // on the platform. Distinct from findErr above, which means we never
      // successfully asked.
      if (!target?.id) continue;

      // .is('cover_image_url', null) repeated on the write, not just the
      // lookup. Splitting one filtered UPDATE into a SELECT and an UPDATE
      // dropped the atomicity the single statement had: without this, a cover
      // set by anything else between the two calls would be overwritten. The
      // window is small and this screen is admin-only, but the guard costs a
      // line and restores the property the original had.
      const { error: syncErr } = await supabase
        .from('books')
        .update({ cover_image_url: v.coverUrl })
        .eq('id', target.id)
        .is('cover_image_url', null);
      if (syncErr) {
        console.warn(`Could not sync cover back to books for "${v.title}":`, syncErr.message);
      }
    }

    await load();
  }

  const counts = {
    pending:  pipelines.filter(p => p.status === 'pending').length,
    approved: pipelines.filter(p => p.status === 'approved').length,
    rejected: pipelines.filter(p => p.status === 'rejected').length,
    hidden:   pipelines.filter(p => p.status === 'hidden').length,
    total:    pipelines.length,
  };

  const TABS = [
    { id: 'pending',  label: 'Pending',  count: counts.pending },
    { id: 'approved', label: 'Approved', count: counts.approved },
    { id: 'rejected', label: 'Rejected', count: counts.rejected },
    { id: 'hidden',   label: 'Hidden',   count: counts.hidden },
    { id: 'all',      label: 'All',      count: counts.total },
  ];

  const byTab = tab === 'all' ? pipelines : pipelines.filter(p => p.status === tab);
  const filtered = wontShow ? byTab.filter(p => flags[p.id]) : byTab;

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '30px 36px', fontFamily: 'var(--font-body)' }}>

      <div style={{ marginBottom: 'var(--space-xl)' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 20 : 26, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -0.6, margin: '0 0 4px' }}>
          Pipelines
        </h1>
        <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>
          Review Popular → Hidden Gem submissions. Pipelines missing a cover on any book land here as Pending until edited and approved.
        </p>
      </div>

      <StatsBar counts={counts} isMobile={isMobile} />

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1.5px solid var(--brand-sand)', marginBottom: 'var(--space-lg)', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setWontShow(false); }} style={{
            fontSize: isMobile ? 12.5 : 13.5, fontWeight: tab === t.id ? 700 : 500,
            padding: isMobile ? '10px 12px' : '12px 18px',
            border: 'none', background: 'transparent',
            color: tab === t.id ? 'var(--neutral-800)' : 'var(--neutral-400)',
            borderBottom: tab === t.id ? '2px solid var(--neutral-800)' : '2px solid transparent',
            marginBottom: -1.5, cursor: 'pointer',
            fontFamily: 'var(--font-body)', whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            {t.label}{t.count > 0 ? ` (${t.count})` : ''}
          </button>
        ))}
      </div>

      {/* Won't show on the homepage: approved pipelines that can never be matched to a craving */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 'var(--space-lg)' }}>
        <button type="button" onClick={() => { setWontShow(v => !v); setTab('approved'); }} disabled={flagCount === null}
          aria-pressed={wontShow}
          style={{
            fontSize: 'var(--font-size-xs)', fontWeight: 700, padding: '6px 12px', borderRadius: 'var(--radius-xxl)',
            border: '1.5px solid var(--feedback-warning-border)',
            background: wontShow ? 'var(--feedback-warning-ink)' : 'var(--feedback-warning-bg)',
            color: wontShow ? 'var(--brand-white)' : 'var(--feedback-warning-ink)',
            cursor: flagCount === null ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-body)',
          }}>
          Won&apos;t show on the homepage{flagCount === null ? '' : ` (${flagCount})`}
        </button>
        <InfoTip text="Approved pipelines that can never appear for any craving: the gem isn't linked to a catalogue book, the gem book isn't live, the gem book has no matching data yet, or a title is blank. Each one says which." width={240} />
        {flagError && (
          <span style={{ fontSize: 'var(--font-size-xxs)', color: '#991b1b' }}>Couldn&apos;t check which pipelines can show: {flagError}</span>
        )}
        {voteError && (
          <span style={{ fontSize: 'var(--font-size-xxs)', color: '#991b1b' }}>Couldn&apos;t read vote counts: {voteError}</span>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>
          Loading pipelines...
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)', fontStyle: 'italic' }}>
          {wontShow ? 'Nothing flagged. Every approved pipeline can show.' : `No ${tab === 'all' ? '' : tab} pipelines yet`}
        </div>
      ) : (
        filtered.map(pl => (
          <PipelineCard
            key={pl.id}
            pipeline={pl}
            working={working}
            onAction={handleAction}
            onEditSave={handleEditSave}
            isMobile={isMobile}
            reasons={flags[pl.id]}
            votes={votes[pl.id]}
            anchor={anchors[pl.id]}
            onNavigate={onNavigate}
          />
        ))
      )}
    </div>
  );
}